import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { ProgressBar } from 'primereact/progressbar';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import { Divider } from 'primereact/divider';
import axiosInstance from '../../../config/axios.config';
import ReportBanner from '../ReportBanner';
import ConsentCheckbox from './ConsentCheckbox';
import GlibGstReturnFetchOutputComponent from '../output/GlibGstReturnFetchOutputComponent';

// Poll cadence for the consent session. The borrower has to key in a GSTIN,
// wait for an OTP and consent, so polling faster buys nothing and the provider
// rate-limits. 10s over the 30-minute session window = 180 polls.
const POLL_MS = 10000;
const MAX_POLLS = 180;

const STAGES = { FORM: 0, WAITING: 1, DONE: 2 };

/**
 * GST Return Fetch — borrower consent journey.
 *
 * Inverted from GlibGstReturnInputComponent: the DSA uploads nothing. We create
 * a session, hand the borrower a one-time link, and poll until they finish
 * consenting — at which point the provider pulls their GSTR-3B filings straight
 * from the GST portal.
 *
 *   1. POST  /glib/gst-fetch/create-session   -> sdk_url the borrower opens
 *   2. borrower completes GSTIN + PAN + OTP + consent   (outside our app)
 *   3. GET   /glib/gst-fetch/{id}/status      -> polled until returns arrive
 *
 * Credits are charged by the backend only when returns actually arrive, so an
 * abandoned journey costs the tenant nothing.
 */
const GlibGstReturnFetchInputComponent = ({
    serviceId: serviceIdProp,
    serviceDetails: serviceDetailsProp,
    leadId,
    initialReportData,
    onReportChange,
} = {}) => {
    const { serviceId: serviceIdParam } = useParams();
    const serviceId = serviceIdProp || serviceIdParam;

    const toast = useRef(null);
    const pollTimerRef = useRef(null);
    const pollCountRef = useRef(0);
    const cancelledRef = useRef(false);

    const [consent, setConsent] = useState(false);
    const [errors, setErrors] = useState({});

    const [stage, setStage] = useState(initialReportData ? STAGES.DONE : STAGES.FORM);
    const [busy, setBusy] = useState(false);
    const [note, setNote] = useState('');

    const [reportId, setReportId] = useState(null);
    const [sdkUrl, setSdkUrl] = useState('');
    const [expiresAt, setExpiresAt] = useState(null);
    const [secondsLeft, setSecondsLeft] = useState(null);
    const [reportData, setReportData] = useState(initialReportData || null);
    const [fatalError, setFatalError] = useState('');
    const [copied, setCopied] = useState(false);

    const [serviceDetails, setServiceDetails] = useState(serviceDetailsProp || null);
    const [fetchingService, setFetchingService] = useState(true);

    // Always fetch fresh credit cost on mount (see CLAUDE.md "Credits Required").
    useEffect(() => {
        if (!serviceId) { setFetchingService(false); return; }
        let alive = true;
        const load = async () => {
            setFetchingService(true);
            try {
                const res = await axiosInstance.get(`/user/report-services/${serviceId}`);
                if (alive && res.data.success && res.data.data) setServiceDetails(res.data.data);
            } catch (error) {
                console.error('Load service details failed:', error?.message || 'Unknown error');
                if (alive) setServiceDetails((prev) => prev || { available_credits: 'N/A', credits_required: 'N/A' });
            } finally {
                if (alive) setFetchingService(false);
            }
        };
        load();
        return () => { alive = false; };
    }, [serviceId]);

    // Stop polling if the component unmounts mid-session (drawer closed, route
    // change) — otherwise the timer keeps firing against a dead component.
    useEffect(() => () => {
        cancelledRef.current = true;
        if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    }, []);

    // Countdown to session expiry. The link dies after 30 minutes, so the DSA
    // needs to know how long the borrower has before it is worth re-sending.
    useEffect(() => {
        if (!expiresAt || stage !== STAGES.WAITING) { setSecondsLeft(null); return undefined; }

        const tick = () => {
            const left = Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000);
            setSecondsLeft(left > 0 ? left : 0);
        };
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [expiresAt, stage]);

    const formatCountdown = (s) => {
        if (s === null || s === undefined) return null;
        if (s <= 0) return 'expired';
        const m = Math.floor(s / 60);
        const sec = s % 60;
        return `${m}:${String(sec).padStart(2, '0')}`;
    };

    /* --------------------------------------------------------------- polling */

    const finishWithReport = useCallback((payload) => {
        setReportData(payload);
        onReportChange?.(payload);
        setStage(STAGES.DONE);
        setBusy(false);
    }, [onReportChange]);

    const poll = useCallback(async (rid) => {
        if (cancelledRef.current) return;

        pollCountRef.current += 1;
        if (pollCountRef.current > MAX_POLLS) {
            setBusy(false);
            setFatalError('Stopped checking after 30 minutes. If the borrower completed the consent, '
                + 'the returns may still arrive — check Report History shortly.');
            return;
        }

        try {
            const res = await axiosInstance.get(`/glib/gst-fetch/${rid}/status`);
            const d = res.data || {};

            if (d.is_complete) {
                finishWithReport(d);
                toast.current?.show({
                    severity: 'success',
                    summary: 'GST returns fetched',
                    detail: `${d.returns_analysed ?? ''} return(s) received`,
                    life: 3000,
                });
                return;
            }

            if (d.is_failed) {
                setBusy(false);
                const parts = [d.message || 'The provider could not fetch these GST returns.'];
                if (d.vendor_error_code) parts.push(`Provider reference: ${d.vendor_error_code}`);
                setFatalError(parts.join(' '));
                return;
            }

            // Still waiting — either for the borrower, or for the provider to
            // finish analysing once consent has landed.
            setNote(d.message || 'Waiting for the borrower…');
            const delay = d.retry_after ? d.retry_after * 1000 : POLL_MS;
            pollTimerRef.current = setTimeout(() => poll(rid), delay);
        } catch (error) {
            console.error('GST fetch status poll failed:', error?.message || 'Unknown error');
            // Transient network blip — keep polling rather than failing the run.
            pollTimerRef.current = setTimeout(() => poll(rid), POLL_MS);
        }
    }, [finishWithReport]);

    /* -------------------------------------------------------------- pipeline */

    const startSession = useCallback(async () => {
        setBusy(true);
        setFatalError('');
        setStage(STAGES.WAITING);
        setNote('Creating the consent link…');

        try {
            const body = { api_provider_service_id: parseInt(serviceDetails?.id || serviceId, 10) };
            if (leadId) body.lead_id = leadId;

            const res = await axiosInstance.post('/glib/gst-fetch/create-session', body);
            const d = res.data || {};
            if (!d.success || !d.report_id) throw new Error(d.message || 'Could not start the consent session');

            setReportId(d.report_id);
            setSdkUrl(d.sdk_url || '');
            setExpiresAt(d.expires_at || null);
            setNote('Share the link with the borrower to continue.');

            pollCountRef.current = 0;
            poll(d.report_id);
        } catch (error) {
            console.error('GST fetch session failed:', error?.message || 'Unknown error');
            setBusy(false);
            const msg = error.response?.data?.message || error?.message || 'Could not start the consent session';
            setFatalError(msg);
            toast.current?.show({ severity: 'error', summary: 'Could not start', detail: msg, life: 4000 });
        }
    }, [serviceId, serviceDetails, leadId, poll]);

    const handleSubmit = (e) => {
        e?.preventDefault();

        if (!consent) {
            setErrors({ consent: 'Please confirm consent before continuing' });
            toast.current?.show({ severity: 'warn', summary: 'Consent required', detail: "Confirm the borrower's consent", life: 3000 });
            return;
        }
        setErrors({});

        const required = serviceDetails?.credits_required;
        const available = serviceDetails?.available_credits;
        if (typeof available === 'number' && typeof required === 'number' && available < required) {
            toast.current?.show({
                severity: 'error',
                summary: 'Insufficient credits',
                detail: `Required: ${required}, Available: ${available}`,
                life: 3500,
            });
            return;
        }

        confirmDialog({
            group: 'glib-gst-fetch-confirm',
            message: (
                <div className="text-sm">
                    <div className="mb-2">A one-time link will be created for the borrower to give consent.</div>
                    <div className="mb-2"><span className="text-600">Available credits:</span> <span className="text-900 font-medium">{available ?? 'N/A'}</span></div>
                    <div className="mb-2"><span className="text-600">Credits to deduct:</span> <span className="text-900 font-medium">{required ?? 'N/A'}</span></div>
                    <div className="text-600 text-xs mt-2">
                        Credits are charged only when the returns actually arrive — an
                        abandoned or expired consent costs nothing.
                    </div>
                </div>
            ),
            header: 'Start GST return fetch',
            icon: 'pi pi-question-circle text-primary pi-2x',
            acceptLabel: 'Yes, create link',
            rejectLabel: 'Cancel',
            accept: startSession,
            style: { width: '90vw', maxWidth: '500px' },
            draggable: false,
        });
    };

    const copyLink = async () => {
        if (!sdkUrl) return;
        try {
            await navigator.clipboard.writeText(sdkUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
            toast.current?.show({ severity: 'success', summary: 'Link copied', life: 2000 });
        } catch (error) {
            console.error('Clipboard write failed:', error?.message || 'Unknown error');
            toast.current?.show({
                severity: 'warn',
                summary: 'Could not copy',
                detail: 'Select the link and copy it manually.',
                life: 3000,
            });
        }
    };

    const cancelSession = async () => {
        if (!reportId) return;
        cancelledRef.current = true;
        if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
        try {
            await axiosInstance.post(`/glib/gst-fetch/${reportId}/cancel`);
            toast.current?.show({ severity: 'info', summary: 'Cancelled', detail: 'No credits were charged.', life: 3000 });
        } catch (error) {
            console.error('Cancel session failed:', error?.message || 'Unknown error');
        }
        handleAnother();
    };

    const handleAnother = () => {
        cancelledRef.current = false;
        if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
        setReportData(null);
        onReportChange?.(null);
        setConsent(false);
        setErrors({});
        setStage(STAGES.FORM);
        setBusy(false);
        setNote('');
        setReportId(null);
        setSdkUrl('');
        setExpiresAt(null);
        setSecondsLeft(null);
        setFatalError('');
    };

    /* ----------------------------------------------------------------- render */

    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog group="glib-gst-fetch-confirm" />
            <div className="grid">
                <div className="col-12">
                    <ReportBanner
                        icon="pi-percentage"
                        title="GST Return Fetch"
                        subtitle="Fetch GSTR-3B returns directly from the GST portal with the borrower's consent"
                        availableCredits={serviceDetails?.available_credits}
                        requiredCredits={serviceDetails?.credits_required}
                        loading={fetchingService}
                    />

                    {stage !== STAGES.DONE && (
                        <Card pt={{ root: { className: 'border-1 surface-200 border-left-3 border-green-500 surface-0' } }}>
                            <form onSubmit={handleSubmit} noValidate>
                                <div className="flex align-items-center gap-2 mb-3">
                                    <i className="pi pi-shield text-primary" aria-hidden="true" />
                                    <span className="text-sm font-semibold text-900 uppercase">Borrower Consent</span>
                                </div>

                                <p className="text-600 text-sm mt-0 mb-3">
                                    No documents needed. We create a secure link for the borrower — they
                                    enter their GSTIN and PAN, verify an OTP, and approve access. Their
                                    GSTR-3B filings are then fetched automatically.
                                </p>

                                <Divider />
                                <div className="grid">
                                    <ConsentCheckbox
                                        checked={consent}
                                        onChange={(e) => { setConsent(e.checked); setErrors({}); }}
                                        error={errors.consent}
                                        reportType="gstin"
                                    />
                                </div>

                                <div className="flex justify-content-end mt-3">
                                    <Button
                                        type="submit"
                                        label="Create Consent Link"
                                        icon="pi pi-arrow-right"
                                        iconPos="right"
                                        loading={busy && stage === STAGES.FORM}
                                        disabled={fetchingService || stage === STAGES.WAITING}
                                        className="w-full md:w-auto"
                                    />
                                </div>
                            </form>
                        </Card>
                    )}

                    {/* Waiting — link to share + live status, inline under the form */}
                    {stage === STAGES.WAITING && (
                        <Card className="mt-3">
                            {fatalError ? (
                                <>
                                    <Message severity="error" text={fatalError} className="w-full mb-3" />
                                    <Button label="Start over" icon="pi pi-refresh" onClick={handleAnother} />
                                </>
                            ) : (
                                <>
                                    {sdkUrl && (
                                        <>
                                            <div className="flex align-items-center gap-2 mb-2">
                                                <i className="pi pi-link text-primary" aria-hidden="true" />
                                                <span className="text-sm font-semibold text-900 uppercase">
                                                    Borrower Consent Link
                                                </span>
                                                {secondsLeft !== null && (
                                                    <Tag
                                                        severity={secondsLeft > 300 ? 'info' : 'warning'}
                                                        icon="pi pi-clock"
                                                        value={`Expires in ${formatCountdown(secondsLeft)}`}
                                                        className="text-xs"
                                                    />
                                                )}
                                            </div>

                                            <p className="text-600 text-xs mt-0 mb-2">
                                                Send this to the borrower, or open it on their device. It works once
                                                and expires with the session.
                                            </p>

                                            <div className="flex flex-column md:flex-row gap-2 mb-3">
                                                <InputText
                                                    value={sdkUrl}
                                                    readOnly
                                                    onFocus={(e) => e.target.select()}
                                                    className="w-full p-inputtext-sm font-mono flex-1"
                                                    aria-label="Borrower consent link"
                                                />
                                                <div className="flex gap-2">
                                                    <Button
                                                        type="button"
                                                        label={copied ? 'Copied' : 'Copy'}
                                                        icon={copied ? 'pi pi-check' : 'pi pi-copy'}
                                                        severity={copied ? 'success' : 'secondary'}
                                                        outlined
                                                        size="small"
                                                        onClick={copyLink}
                                                        className="flex-1 md:flex-none"
                                                    />
                                                    <Button
                                                        type="button"
                                                        label="Open"
                                                        icon="pi pi-external-link"
                                                        size="small"
                                                        onClick={() => window.open(sdkUrl, '_blank', 'noopener,noreferrer')}
                                                        className="flex-1 md:flex-none"
                                                    />
                                                </div>
                                            </div>
                                            <Divider />
                                        </>
                                    )}

                                    <div className="flex align-items-center gap-3">
                                        <i className="pi pi-spin pi-spinner text-primary pi-2x flex-shrink-0" aria-hidden="true" />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-900 text-sm font-semibold mb-1">
                                                {note || 'Waiting for the borrower…'}
                                            </div>
                                            <ProgressBar
                                                mode="indeterminate"
                                                style={{ height: '0.375rem' }}
                                                aria-label="Waiting for borrower consent"
                                            />
                                            <div className="text-500 text-xs mt-2">
                                                This page updates automatically once the borrower finishes — keep it open.
                                            </div>
                                        </div>
                                        <Button
                                            type="button"
                                            label="Cancel"
                                            icon="pi pi-times"
                                            severity="secondary"
                                            text
                                            size="small"
                                            onClick={cancelSession}
                                            className="flex-shrink-0"
                                        />
                                    </div>
                                </>
                            )}
                        </Card>
                    )}

                    {/* Results — same output component as the upload flow */}
                    {stage === STAGES.DONE && (
                        <>
                            <div className="flex justify-content-end mb-3">
                                <Button
                                    label="Submit another"
                                    icon="pi pi-refresh"
                                    severity="secondary"
                                    outlined
                                    size="small"
                                    onClick={handleAnother}
                                />
                            </div>
                            <GlibGstReturnFetchOutputComponent reportData={reportData} />
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default GlibGstReturnFetchInputComponent;
